// THE EMAIL-GROUP LISTING'S FOURTH COLUMN — "Possible Ongoing match" (busy-my-web#370, Figma
// section 40006949-281541, measured on the 40010685:353763 instance; the same column is drawn all
// through 40010659-342529, the section busy-my-web#355 reviews).
//
// ── WHAT CHANGED IN THE DESIGN ──────────────────────────────────────────────────────────────────
// busy-my-web#317 asked the possible-Ongoing question as one CARD per match, in the middle of the
// Email Workspace (components/Email/PossibleMatchCards.vue). The updated section asks it in the
// group LISTING instead: the same four groups the workspace already lists, each with the Ongoing it
// may belong to as a chip and three answers on the right —
//
//     GitHub    18 emails   10 May   ⊙ Programming     View   No match   Connect
//     HMRC       4 emails   11 Mar   ✉ Health issu…    View   No match   Connect
//
// So this module is the JOIN, and only the join: which of the possible-match cards (already grouped
// by lib/possibleMatches.js) belongs to which row of the sender listing. Nothing new is asked of the
// server, no second grouping is invented, and answering the row is still `addSuggestedThread` /
// `rejectSuggestedThread` on the very same edges the cards answer — one question, one answer, two
// places it can be drawn.
//
// Pure: no store, no router, nothing mounted.

import { THREAD_TEXT } from '@/lib/emailThreadActions'

/** Verbatim labels, in the frame's own words and order. */
export const MATCH_TEXT = {
  // The column's heading (frame node 40010685:353795).
  column: 'Possible Ongoing match',
  // The three actions on a row that HAS an offer (nodes 40010752:356350 / :356354 / :356358).
  view: 'View',
  noMatch: 'No match',
  connect: 'Connect',
  // …and the one on a row that has none. The frames only draw rows with an offer, so the wording is
  // not invented here: it is the Work-board's own verb for the very same act (busy-my-web#274,
  // lib/emailThreadActions.js), which is what the "add a group to an Ongoing that already exists"
  // flow this item asks for runs on.
  addToOngoing: THREAD_TEXT.addToOngoing,
}

/** A sender, in the one shape both lists can be compared on. */
const key = (value) => String(value || '').trim().toLowerCase()

/**
 * The offer standing against ONE group row, or null.
 *
 * Matched on the SENDER, which is the only thing the two lists share: a sender group is keyed by
 * the raw `From` header the mailbox grouped on, and a match card carries the display name parsed
 * out of that same header (lib/possibleMatches.js's `senderName`). So the row's own display name is
 * compared as well as its raw sender — a card built from a header the service could not name still
 * finds its row, and a row whose group has no sender at all matches nothing rather than matching
 * the first card in the list.
 *
 * The FIRST card wins where a sender has several: the list arrives in the server's confidence
 * ranking, and a column with room for one chip must show the best offer, not an arbitrary one.
 */
export function groupMatch(row, cards = []) {
  const sender = key(row?.sender)
  const name = key(row?.name)
  if (!sender && !name) return null
  return (cards || []).find((c) => {
    const cardSender = key(c?.sender)
    if (!cardSender) return false
    return cardSender === sender || cardSender === name
  }) || null
}

/**
 * The listing, each row carrying its own offer.
 *
 * A row keeps every field it had — the view renders one list, not a list plus a lookup — and gains
 * `match`, which is null for most of them: most of a mailbox is mail nobody has suggested anything
 * about, and those rows draw the column's actions without a chip.
 */
export function groupMatchRows(rows = [], cards = []) {
  return (rows || []).filter(Boolean).map(row => ({ ...row, match: groupMatch(row, cards) }))
}

/**
 * What the row's chip SAYS — the Ongoing the offer names.
 *
 * Empty where the offer named no story, which is the real payload today for a thread-row suggestion
 * (see the #317 report's data gaps): the chip is then simply not drawn, rather than drawn empty.
 */
export function matchChipLabel(match) {
  return String(match?.category || '').trim()
}

/**
 * The edges a row's answer acts on — one dispatch per edge, exactly as the cards do it.
 *
 * Deduplicated and emptied of blanks, so a card carrying a half-built offer cannot make the view
 * fire a request with no id in it.
 */
export function matchRelationshipIds(match) {
  const seen = new Set()
  for (const id of match?.relationshipIds || []) {
    const value = String(id ?? '').trim()
    if (value) seen.add(value)
  }
  return [...seen]
}

/**
 * Can this row be connected to an Ongoing that already exists, without an offer to accept?
 *
 * That is the new flow this item adds, and it runs on the Work-board's existing machinery: open the
 * group so its threads are read, then hand those threads to the ongoing chooser
 * (`emailSession/openOngoingChooserFor` → `addThreadsToOngoing`, busy-my-web#274). It needs a
 * sender to open the group by and nothing else — the group listing's payload carries no timeline
 * ids of its own, which is why the threads have to be read first.
 */
export function canAddToOngoing(row) {
  return !!key(row?.sender) && !row?.match
}

/** The timeline ids a fetched group stands for — what the chooser is opened about. */
export function groupTimelineIds(emails = []) {
  const seen = new Set()
  for (const mail of emails || []) {
    const id = String(mail?.id ?? '').trim()
    if (id) seen.add(id)
  }
  return [...seen]
}

// ── The MATCHED groups' own chooser (busy-my-web#422, frames 40010687:355542 / 40010765:356656) ──
//
// Ticking a group that HAS a possible match does not ask the archive question: the section replaces
// "What would you like to do?"'s three verbs with the four the match itself implies —
//
//     Connect to suggested Ongoing (Recommended) · Dismiss suggested match
//     · Connect to another Ongoing · Create new Ongoing
//
// The wording pluralises with the selection (the frame writes "Ongoings" and "matches" over three
// ticked groups), because it names what is about to happen to the things in hand.

/** Verbatim, in the frames' words and their order. */
export const CHOOSER_TEXT = {
  connectSuggested: (n) => `Connect to suggested Ongoing${Number(n) === 1 ? '' : 's'}`,
  dismiss: (n) => `Dismiss suggested match${Number(n) === 1 ? '' : 'es'}`,
  connectAnother: 'Connect to another Ongoing',
  createNew: 'Create new Ongoing',
  // …and the fourth option the OPEN group's chooser gains (40010799:360028).
  addToOngoing: 'Add to Ongoing',
}

/** The option ids, so the chooser, the CTA and the commit name them once. */
export const MATCH_CHOICE = {
  connect: 'matches-connect',
  dismiss: 'matches-dismiss',
  another: 'matches-connect-another',
  addToOngoing: 'group-add-ongoing',
}

/**
 * The ticked GROUPS that have an offer standing against them, each paired with it.
 *
 * A board pick carries what was drawn (sender + name + count), and a match card carries the sender
 * the offer was classified from — so the join is `groupMatch`'s, the very same one the listing's
 * fourth column is built with. Picks that are threads or attachments are not groups and are ignored;
 * a group nobody has suggested anything about simply is not in the answer, which is what puts the
 * selection back on the ordinary archive verbs.
 */
export function matchedGroupPicks(items = [], cards = []) {
  const out = []
  for (const pick of items || []) {
    if (!pick || pick.kind !== 'email-group') continue
    const match = groupMatch({ sender: pick.sender, name: pick.title }, cards)
    if (match) out.push({ pick, match })
  }
  return out
}

/** "HMRC, HSBC" — the groups an answer is about, named in the order they were ticked. */
export function matchedGroupNames(matched = []) {
  return (matched || []).map(m => String(m?.pick?.title || m?.pick?.sender || '').trim()).filter(Boolean)
}

/** Every edge the answer acts on, across every matched group, deduplicated. */
export function matchedRelationshipIds(matched = []) {
  const seen = new Set()
  for (const m of matched || []) {
    for (const id of matchRelationshipIds(m?.match)) seen.add(id)
  }
  return [...seen]
}

/**
 * "HMRC groups is connecting to the ongoing....." (40010766:357940) — the frame's own sentence,
 * grammar and full stops included, because it is the design's words and not a template's.
 */
export function connectingMatchNotice(names = []) {
  const who = (names || []).filter(Boolean).join(', ')
  return { kind: 'running', text: `${who || 'The selected'} groups is connecting to the ongoing.....` }
}

/**
 * "HMRC connected to Health issues" (40010788:358603) — GREEN, with a "View" that opens the
 * Ongoing (busy-my-web#496 / #494, 2026-09-15 parity audit F2-1 findings 13 / 16). `tone` and
 * `view` are read by the banner every session notice rides (EmailInboxPanel.vue).
 */
export function connectedMatchNotice(names = [], ongoingTitle = '', ongoingId = '') {
  const who = (names || []).filter(Boolean).join(', ') || 'The selected groups'
  const to = String(ongoingTitle || '').trim()
  const id = String(ongoingId || '').trim()
  return {
    kind: 'done',
    tone: 'success',
    view: id ? `/ongoing/${id}` : '',
    text: to ? `${who} connected to ${to}` : `${who} connected to the ongoing`,
  }
}

/**
 * The rail's receipt after a match is accepted (frames 40010788:358603 / 40010930:373966):
 * "HMRC email group / Connected to the ongoing / Sep 3 8:30 PM / Connected / Health issues . ongoing".
 * The same entry shape the picker path writes (lib/ongoingPicker's `connectReceipt`) with the
 * frame's own detail word, so the rail has ONE kind of "connected" entry however the connection
 * was made. `connectSuggestedGroups` never wrote one at all — finding 14.
 */
export function matchReceipt({ names = [], ongoingTitle = '', at = new Date() } = {}) {
  const when = at instanceof Date ? at : new Date(at)
  const who = (names || []).filter(Boolean).join(', ')
  const to = String(ongoingTitle || '').trim()
  return {
    id: `email-match:${when.getTime()}`,
    icon: 'sms-tracking',
    title: who ? `${who} email group` : 'Email group',
    description: 'Connected to the ongoing',
    date: new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(when),
    time: new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: true }).format(when)
      .toLowerCase().replace(/\s?(am|pm)/, ' $1'),
    detail: 'Connected',
    label: who,
    rows: to ? [{ id: to, icon: 'ongoing', text: `${to} · ongoing` }] : [],
  }
}

/** What a dismissal says. The frames draw no banner for it, so it uses the session's plain voice. */
export function dismissedMatchNotice(n) {
  const count = Number(n) || 0
  return { kind: 'done', text: `${count} suggested match${count === 1 ? '' : 'es'} dismissed` }
}

export default groupMatchRows
